import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Building, Calendar, MapPin } from 'lucide-react';
import { sendEmail } from '../services/emailService';

const EventRegistration = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    designation: '',
    experience: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // Sample event data - in real app, fetch by eventId
  const events = {
    1: { title: 'International AI Conference 2024', type: 'conference', date: 'March 15-17, 2024', venue: 'Tech Convention Center' },
    2: { title: 'Excellence in Research Award', type: 'award', date: 'April 20, 2024', venue: 'Grand Auditorium' },
    3: { title: 'Machine Learning Workshop', type: 'workshop', date: 'May 10, 2024', venue: 'Innovation Lab' },
    4: { title: 'Faculty Development Program', type: 'fdp', date: 'June 5-7, 2024', venue: 'Academic Center' }
  };

  const event = events[eventId] || events[1];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    if (!formData.organization.trim()) newErrors.organization = 'Organization is required';
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      const emailData = {
        to: 'nirmala21549@gmail.com',
        subject: `Event Registration - ${event.title}`,
        html: `
          <h2>New Event Registration</h2>
          <p><strong>Event:</strong> ${event.title}</p>
          <p><strong>Event Date:</strong> ${event.date}</p>
          <p><strong>Venue:</strong> ${event.venue}</p>
          <hr>
          <h3>Participant Details:</h3>
          <p><strong>Full Name:</strong> ${formData.fullName}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Phone:</strong> ${formData.phone}</p>
          <p><strong>Organization:</strong> ${formData.organization}</p>
          <p><strong>Designation:</strong> ${formData.designation}</p>
          <p><strong>Experience:</strong> ${formData.experience || 'Not specified'}</p>
        `
      };
      
      await sendEmail(emailData);
      setSubmitStatus('success');
      setTimeout(() => navigate('/events'), 2000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getEventColor = (type) => {
    const colors = {
      conference: '#3182ce',
      award: '#d69e2e',
      workshop: '#38a169',
      fdp: '#805ad5'
    };
    return colors[type] || '#3182ce';
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', paddingTop: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <button
            onClick={() => navigate('/events')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              color: getEventColor(event.type),
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            <ArrowLeft size={20} />
            Back to Events
          </button>
          
          <div style={{
            background: `linear-gradient(135deg, ${getEventColor(event.type)}20 0%, ${getEventColor(event.type)}10 100%)`,
            padding: '30px',
            borderRadius: '15px',
            marginBottom: '30px'
          }}>
            <h1 style={{ color: '#2d3748', fontSize: '2rem', fontWeight: 700, marginBottom: '10px' }}>
              Event Registration
            </h1>
            <h2 style={{ color: getEventColor(event.type), fontSize: '1.5rem', fontWeight: 600, marginBottom: '15px' }}>
              {event.title}
            </h2>
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} color={getEventColor(event.type)} />
                <span style={{ color: '#4a5568', fontWeight: 600 }}>{event.date}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={16} color={getEventColor(event.type)} />
                <span style={{ color: '#4a5568', fontWeight: 600 }}>{event.venue}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '30px' }}>
            Registration Details
          </h3>

          {submitStatus === 'success' && (
            <div style={{
              backgroundColor: '#c6f6d5',
              color: '#22543d',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center',
              fontWeight: 600
            }}>
              Registration successful! Redirecting to events page...
            </div>
          )}

          {submitStatus === 'error' && (
            <div style={{
              backgroundColor: '#fed7d7',
              color: '#c53030',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center',
              fontWeight: 600
            }}>
              Registration failed. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: '25px' }}>
              {/* Full Name */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  <User size={16} />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.fullName ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = errors.fullName ? '#e53e3e' : '#e2e8f0'}
                />
                {errors.fullName && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.fullName}</span>}
              </div>

              {/* Email */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  <Mail size={16} />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.email ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#e53e3e' : '#e2e8f0'}
                />
                {errors.email && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.email}</span>}
              </div>

              {/* Phone */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  <Phone size={16} />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.phone ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = errors.phone ? '#e53e3e' : '#e2e8f0'}
                />
                {errors.phone && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.phone}</span>}
              </div>

              {/* Organization */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  <Building size={16} />
                  Organization *
                </label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.organization ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = errors.organization ? '#e53e3e' : '#e2e8f0'}
                />
                {errors.organization && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.organization}</span>}
              </div>

              {/* Designation */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  Designation *
                </label>
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.designation ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = errors.designation ? '#e53e3e' : '#e2e8f0'}
                />
                {errors.designation && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.designation}</span>}
              </div>

              {/* Experience */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2d3748', fontWeight: 600, marginBottom: '8px' }}>
                  Years of Experience
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = getEventColor(event.type)}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="">Select experience</option>
                  <option value="0-1">0-1 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11-15">11-15 years</option>
                  <option value="15+">15+ years</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: isSubmitting ? '#a0aec0' : getEventColor(event.type),
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  marginTop: '10px'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Complete Registration'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventRegistration;